import * as React from "react";
import {Box, Button, Icon, Link, Select, SpaceBetween, Spinner, Table} from "@amzn/awsui-components-react";
import {TestCase, UATProject, UATStatus} from "../../types/UATProject";
import {useState} from "react";
import {TestCaseSummaryModal} from "./TestRunViewModal";
import {AxiosResponse} from "axios";
import {DownloadS3FileApiFactory} from "../../lusca-search-api/LuscaSearchApiFactory";
import {DownloadS3FileResponse} from "../../lusca-search-api/generated-src";
import {useMutation} from "@tanstack/react-query";
import {updateUATProject, updateTestCaseAttribute, fetchUATProject} from "../../helpers/RavenUtils";

interface TestRunsViewProps {
  project: UATProject;
  onProjectUpdate?: (updatedProject: UATProject) => void;
}

export default function TestRunsView( { project, onProjectUpdate } : TestRunsViewProps) {
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState<TestCase | null>(null);
  const [currentProject, setCurrentProject] = useState<UATProject>(project);
  const downloadS3FileApi = DownloadS3FileApiFactory();

  // Update currentProject when project prop changes
  React.useEffect(() => {
    if (project && project.id) {
      setCurrentProject(project);
    }
  }, [project]);

  const { mutate, isLoading, error } = useMutation<
    DownloadS3FileResponse,
    Error,
    { key: string }
  >(
    async ({ key }) => {
      const response: AxiosResponse<DownloadS3FileResponse> =
        await downloadS3FileApi.download(key);
      console.log(response.data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        window.location.href = data.url; // download file
      },
      onError: (err) => {
        console.error("Error:", err);
      },
    }
  );

  const statusOptions = Object.entries(UATStatus)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key]) => ({
      label: key,
      value: UATStatus[key as keyof typeof UATStatus].toString(),
    }));

    return (
        <>
        <Table
            renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
                `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
            }
            columnDefinitions={[
                {
                    id: "name",
                    header: "Name",
                    width: 240,
                    cell: (testCase: TestCase) => (
                        <Link
                            href="#"
                            onFollow={e => {
                              e.preventDefault();
                              setSelectedTestCase(testCase);
                              setModalVisible(true);
                            }}
                        >
                          {testCase.name}
                        </Link>
                    ),
                    sortingField: "name",
                    isRowHeader: true
                },
                {
                    id: "runCount",
                    header: "Runs",
                    width: 90,
                    cell: testCase => testCase.history?.length,
                    sortingField: "runCount",
                    isRowHeader: true
                },
                {
                  id: "orderIDs",
                  header: "Transaction",
                  width: 320,
                  cell: (testCase) => (
                      <Link
                          href="#"
                          onFollow={(e) => {
                            e.preventDefault();
                            const baseUrl = `${
                                window.location.href.split("4443/")[0]
                            }4443/`;
                            window.open(
                                `${baseUrl}?stage=${testCase.order.stage}&transactionId=${testCase.order.id}`,
                                "_blank"
                            );
                          }}
                      >
                        {testCase.order.id}
                      </Link>
                  ),
                  sortingField: "orderIDs",
                  isRowHeader: true,
                },
                {
                    id: "status",
                    header: "Status",
                    width: 220,
                    cell: (testCase) => (
                        <div
                            onClick={e => e.stopPropagation()}
                            onMouseDown={e => e.stopPropagation()}
                            style={{position: 'relative', zIndex: 500}}
                        >
                            <Select
                                selectedOption={{
                                    label: UATStatus[testCase.status],
                                    value: testCase.status.toString()
                                }}
                                onChange={({detail}) => {
                                  if (detail.selectedOption.value && currentProject && currentProject.id) {
                                        const newStatus = parseInt(detail.selectedOption.value) as UATStatus;
                                        const success = updateTestCaseAttribute(
                                            currentProject.id,
                                            { name: testCase.name, orderId: testCase.order.id },
                                            "status",
                                            newStatus
                                        );
                      if (success) {
                        // Refresh the project data from localStorage
                        const updatedProject = fetchUATProject(currentProject.id);
                        if (updatedProject) {
                          setCurrentProject(updatedProject);
                          onProjectUpdate?.(updatedProject);
                        }
                      } else {
                        console.error("Failed to update test case status");
                      }
                    }
                  }}
                  options={statusOptions}
                  placeholder="Select status"
                  expandToViewport={true}
                />
              </div>
            ),
            sortingField: "status",
          },
          {
            id: "runTest",
            header: "Run Test",
            width: 140,
            cell: (testCase) => {
              const [isLoading, setIsLoading] = useState(false);

                        const handleRunTest = async () => {
                            setIsLoading(true);
                            setTimeout(() => {
                                setIsLoading(false);
                            }, 2000);
                        };

                  return (
                      <div style={{zIndex: 500}}>
                        <Button
                            onClick={handleRunTest}
                            disabled={isLoading}
                            ariaLabel="Run test"
                        >
                          {isLoading ? (
                              <Spinner size="normal"/>
                          ) : (
                              <>
                                <Icon
                                    name="caret-right-filled"
                                    variant="success"
                                    size={"normal"}
                                />
                                <span>Run</span>
                              </>
                          )}
                        </Button>
                      </div>
                  );
                },
                sortingField: "runTest"
              },
              {
                                id: "downloadFile",
                                header: "Download File",
                                width: 150,
                                cell: testCase => {
                                  const [isLoading, setIsLoading] = useState(false);

                        const handleDownloadfile = async () => {
                          mutate({ key: "uat-summary-report.csv" });
                        };

                      return (
                          <div style={{ zIndex: 1000 }}>
                            <Button
                                onClick={handleDownloadfile}
                                disabled={isLoading}
                                ariaLabel="Download Result"
                            >
                              {isLoading ? (
                                  <Spinner size="normal" />
                              ) : (
                                  <>
                                    <Icon name="download" size="normal" />
                                    <span>Result</span>
                                  </>
                              )}
                            </Button>
                          </div>
                      );
                    },
                  sortingField: "downloadFile",
                },
            ]}
            items={currentProject.testCases}
            loadingText="Loading resources"
            resizableColumns
            empty={
              <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                <SpaceBetween size="m">
                  <b>No resources</b>
                  <Button>Create resource</Button>
                </SpaceBetween>
              </Box>
            }
        />
          <TestCaseSummaryModal
              visible={isModalVisible}
              onDismiss={() => {
                setModalVisible(false);
                setSelectedTestCase(null);
              }}
              testCase={selectedTestCase}
          />
        </>
    );
}
